import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUserCircle,
  FaEdit,
  FaSave,
  FaSignOutAlt,
  FaCamera,
  FaHome,
  FaShoppingCart,
  FaBars,
  FaTimes,
  FaChevronDown,
} from "react-icons/fa";

import { auth, db, storage } from "../firebase";
import { onAuthStateChanged, updateProfile, signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

import { useCart } from "../cart/CartContext";
import "../styles/WeddingPage.css"; // keep your existing stylesheet

/** Services menu links */
const servicesLinks = [
  { to: "/wedding", label: "Wedding Planning" },
  { to: "/design", label: "Design & Decoration" },
  { to: "/destination", label: "Destination Wedding Venues" },
  { to: "/entertainment", label: "Entertainment" },
  { to: "/others", label: "Custom Events" },
];

// ---- Helpers ----
function normalizeStatus(s) {
  const v = (s || "ongoing").toLowerCase();
  if (["completed", "paid", "captured", "success", "settled"].includes(v))
    return "completed";
  if (["cancelled", "failed", "refunded", "void"].includes(v))
    return "cancelled";
  return "ongoing";
}

function prettyOrderId(o) {
  const raw = (o.orderId || o.id || "").toString();
  if (!raw) return "—";
  const core = raw.replace(/^order_/, "");
  return "ORD-" + core.slice(-8).toUpperCase();
}

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function ProfilePage() {
  const { cart } = useCart();
  const navigate = useNavigate();

  // Auth/profile state
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    mobile: "",
    photoURL: "",
  });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Navbar/menus
  const [dropdown, setDropdown] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const dropdownTimeout = useRef(null);
  const nameRef = useRef(null);

  // Live orders
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all"); // all | ongoing | completed | cancelled

  /** Auth + profile + realtime orders */
  useEffect(() => {
    let unsubOrders = null;

    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        setUser(firebaseUser);

        if (!firebaseUser) {
          setOrders([]);
          setLoading(false);
          if (unsubOrders) {
            unsubOrders();
            unsubOrders = null;
          }
          return;
        }

        // Load profile doc
        try {
          const snap = await getDoc(doc(db, "users", firebaseUser.uid));
          setProfile({
            name:
              (snap.exists() && snap.data().name) ||
              firebaseUser.displayName ||
              "",
            email: firebaseUser.email || "",
            mobile: (snap.exists() && snap.data().mobile) || "",
            photoURL:
              (snap.exists() && snap.data().photoURL) ||
              firebaseUser.photoURL ||
              "",
          });
        } catch (e) {
          console.error("Profile load error:", e);
        } finally {
          setLoading(false);
        }

        // Live orders (most recent first)
        const listRef = collection(db, "orders", firebaseUser.uid, "list");
        const qy = query(listRef, orderBy("createdAt", "desc"));
        setOrdersLoading(true);

        if (unsubOrders) unsubOrders();
        unsubOrders = onSnapshot(
          qy,
          (snap) => {
            const rows = [];
            snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
            setOrders(rows);
            setOrdersLoading(false);
          },
          (err) => {
            console.error("Orders snapshot error:", err);
            setOrdersLoading(false);
          }
        );
      } catch (err) {
        console.error("Auth state error:", err);
        setLoading(false);
      }
    });

    return () => {
      if (unsubOrders) unsubOrders();
      unsubAuth();
    };
  }, []);

  // Cleanup pending dropdown timeout on unmount
  useEffect(() => {
    return () => clearTimeout(dropdownTimeout.current);
  }, []);

  useEffect(() => {
    if (editing && nameRef.current) nameRef.current.focus();
  }, [editing]);

  /** Upload new avatar preview */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setProfile((p) => ({ ...p, photoURL: URL.createObjectURL(file) }));
  };

  /** Save profile to Firestore + Auth */
  async function handleSave() {
    if (!user) return;
    if (!profile.name.trim()) return alert("Name cannot be empty.");
    if (profile.mobile && !/^[6-9]\d{9}$/.test(profile.mobile))
      return alert("Enter a valid 10-digit mobile number.");

    let uploadedURL = profile.photoURL;

    // Upload image if changed
    if (photoFile) {
      setUploading(true);
      try {
        const ext = (photoFile.name.split(".").pop() || "jpg").toLowerCase();
        const path = `users/${user.uid}/profile-photo.${ext}`;
        const r = storageRef(storage, path);
        await uploadBytes(r, photoFile);
        uploadedURL = await getDownloadURL(r);
      } catch (e) {
        console.error(e);
        alert("Image upload failed.");
        setUploading(false);
        return;
      }
      setUploading(false);
      setPhotoFile(null);
    }

    // Merge into user doc
    await setDoc(
      doc(db, "users", user.uid),
      {
        name: profile.name.trim(),
        mobile: profile.mobile.trim(),
        photoURL: uploadedURL,
      },
      { merge: true }
    );

    // Also update Firebase Auth displayName/photo
    await updateProfile(user, {
      displayName: profile.name.trim(),
      photoURL: uploadedURL || null,
    });

    setEditing(false);
    setProfile((p) => ({ ...p, photoURL: uploadedURL }));
  }

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  /** Filtered orders */
  const filteredOrders =
    statusFilter === "all"
      ? orders
      : orders.filter((o) => normalizeStatus(o.status) === statusFilter);

  /** Status Pill */
  const statusBadge = (statusRaw) => {
    const status = normalizeStatus(statusRaw);
    const base = {
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: ".02em",
      whiteSpace: "nowrap",
    };
    if (status === "completed")
      return (
        <span style={{ ...base, background: "#e8f7ee", color: "#1e7e34" }}>
          Completed
        </span>
      );
    if (status === "cancelled")
      return (
        <span style={{ ...base, background: "#fdecea", color: "#d93025" }}>
          Cancelled
        </span>
      );
    return (
      <span style={{ ...base, background: "#fff4e5", color: "#c77800" }}>
        Ongoing
      </span>
    );
  };

  /** Hidden-iframe print (prevents .html download & popup blocks) */
  function printHTML(htmlString) {
    const blob = new Blob([htmlString], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.onload = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } finally {
          setTimeout(() => {
            URL.revokeObjectURL(url);
            iframe.remove();
          }, 500);
        }
      }, 150);
    };
    iframe.src = url;
    document.body.appendChild(iframe);
  }
// put these near the imports/top of the file
const BRAND_NAME = "Parivar Wedding Event";
const SUPPORT_EMAIL = "hello@parivarwedding.in";

  /** Receipt generator (uses printHTML) */
  function downloadReceipt(order) {
  const items = Array.isArray(order.items) ? order.items : order.cart || [];
  const created =
    order.createdAt?.toDate?.() ??
    (order.createdAt?._seconds ? new Date(order.createdAt._seconds * 1000) : new Date());
  const dateStr = created.toLocaleString();

  const itemsHtml = items.length
    ? items
        .map((it) => {
          const name = (it?.name || "Item").toString().replace(/</g, "<");
          const price = rupee(it?.price || 0);
          return `<tr><td>${name}</td><td class="right">${price}</td></tr>`;
        })
        .join("")
    : `<tr><td>Package</td><td class="right">${rupee(order.total)}</td></tr>`;

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${BRAND_NAME} – Receipt ${prettyOrderId(order)}</title>
<style>
  :root { --ink:#222; --muted:#666; --line:#eee; --brand:#670D2B; }
  body { font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color:var(--ink); margin:0; background:#fff; }
  .wrap { max-width: 720px; margin: 32px auto; padding: 24px; border:1px solid var(--line); border-radius: 12px; }
  .brand { font-size: 28px; color:var(--brand); font-weight:800; letter-spacing:.5px; }
  .muted { color:var(--muted); }
  .row { display:flex; justify-content:space-between; gap:16px; flex-wrap:wrap; }
  .h { margin: 8px 0; }
  table { width:100%; border-collapse: collapse; margin-top: 16px; }
  th, td { padding: 10px; border-bottom:1px solid #f0f0f0; text-align:left; }
  th { background:#fafafa; }
  .right { text-align:right; }
  .total { font-size: 18px; font-weight:800; color:#b0354c; }
  .footer { margin-top: 24px; font-size: 12px; color:#777; }
  .badge { display:inline-block; padding:4px 10px; border-radius:999px; font-size:12px; font-weight:700; text-transform:uppercase; }
  .b-complete { background:#e8f7ee; color:#1e7e34; }
  .b-cancel { background:#fdecea; color:#d93025; }
  .b-ongo { background:#fff4e5; color:#c77800; }
  @media print { .actions { display:none; } body { margin:0; } }
</style>
</head>
<body>
  <div class="wrap">
    <div class="row">
      <div class="brand">${BRAND_NAME}</div>
      <div class="muted">Receipt • ${dateStr}</div>
    </div>

    <div class="row h">
      <div>
        <div><strong>Billed To:</strong></div>
        <div>${(profile.name || "-").toString().replace(/</g, "<")}</div>
        <div class="muted">${(profile.email || "-").toString().replace(/</g, "<")}</div>
        <div class="muted">${(profile.mobile || "-").toString().replace(/</g, "<")}</div>
      </div>
      <div>
        <div><strong>Order:</strong> ${(order.orderId || order.id || "-").toString().replace(/</g, "<")}</div>
        <div><strong>Payment:</strong> ${(order.paymentId || "-").toString().replace(/</g, "<")}</div>
        <div><strong>Status:</strong>
          <span class="badge ${normalizeStatus(order.status) === "completed" ? "b-complete" : normalizeStatus(order.status) === "cancelled" ? "b-cancel" : "b-ongo"}">${normalizeStatus(order.status).toUpperCase()}</span>
        </div>
      </div>
    </div>

    <table>
      <thead>
        <tr><th>Item</th><th class="right">Price</th></tr>
      </thead>
      <tbody>
        ${itemsHtml}
      </tbody>
      <tfoot>
        <tr><td class="right"><strong>Total</strong></td><td class="right total">${rupee(order.total)}</td></tr>
      </tfoot>
    </table>

    <div class="footer">
      This is a system-generated receipt from <strong>${BRAND_NAME}</strong>.
      For support, contact ${SUPPORT_EMAIL}.
    </div>

    <div class="actions" style="margin-top:16px;">
      <button onclick="window.print()" style="padding:8px 14px;border:1px solid #ddd;border-radius:8px;background:#fafafa;cursor:pointer">Print / Save as PDF</button>
    </div>
  </div>
</body>
</html>`.trim();

  printHTML(html);
}

  if (loading)
    return (
      <div style={{ padding: "80px", textAlign: "center" }}>
        Loading profile...
      </div>
    );
  if (!user)
    return (
      <div style={{ padding: "80px", textAlign: "center" }}>
        You are not logged in.
      </div>
    );

  return (
    <div className="profile-page" style={{ overflowX: "hidden" }}>
      {/* ===== NAVBAR (desktop) ===== */}
      <header className="desktop-navbar">
        <div className="navbar-container">
          <div
            className="navbar-brand"
            onClick={() => navigate("/")}
            style={{ fontFamily: "'Great Vibes', cursive" }}
          >
            Parivar
          </div>

          <nav className="center-nav">
            <Link to="/" className="nav-link">
              <FaHome /> Home
            </Link>

            <div
              className="dropdown"
              onMouseEnter={() => {
                clearTimeout(dropdownTimeout.current);
                setDropdown(true);
              }}
              onMouseLeave={() => {
                dropdownTimeout.current = setTimeout(
                  () => setDropdown(false),
                  200
                );
              }}
            >
              <button className="dropdown-btn">
                Services <FaChevronDown />
              </button>
              <div className={`dropdown-menu ${dropdown ? "show" : ""}`}>
                {servicesLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="dropdown-link"
                    onClick={() => setDropdown(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          <div className="nav-right">
            <Link to="/cart" className="cart-link">
              <FaShoppingCart size={18} />
              {cart.length > 0 && (
                <span className="cart-badge">{cart.length}</span>
              )}
            </Link>
          </div>

          <div className="mobile-menu-icon" onClick={() => setNavOpen(!navOpen)}>
            {navOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </div>
        </div>
      </header>

      {/* ===== MOBILE SIDEBAR ===== */}
      {navOpen && (
        <div className="sidebar-overlay" onClick={() => setNavOpen(false)}>
          <div className="sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="sidebar-header">
              <span>Menu</span>
              <FaTimes onClick={() => setNavOpen(false)} />
            </div>
            <div
              className="sidebar-link"
              onClick={() => {
                navigate("/");
                setNavOpen(false);
              }}
            >
              <FaHome /> Home
            </div>
            <div
              className="sidebar-link"
              onClick={() => setDropdown((d) => !d)}
            >
              <FaChevronDown /> Services
            </div>
            {dropdown && (
              <div className="sidebar-submenu">
                {servicesLinks.map((link) => (
                  <div
                    key={link.to}
                    className="sidebar-submenu-item"
                    onClick={() => {
                      navigate(link.to);
                      setNavOpen(false);
                    }}
                  >
                    {link.label}
                  </div>
                ))}
              </div>
            )}
            <div
              className="sidebar-link"
              onClick={() => {
                navigate("/cart");
                setNavOpen(false);
              }}
            >
              <FaShoppingCart /> Cart
            </div>
          </div>
        </div>
      )}

      {/* ===== PROFILE + ORDERS LAYOUT ===== */}
      <div
        id="profileGrid"
        style={{
          maxWidth: 1100,
          margin: "120px auto 40px",
          gap: 24,
          padding: "0 16px",
        }}
      >
        {/* PROFILE CARD */}
        <div
          className="profile-card"
          style={{
            background: "#fff",
            borderRadius: 20,
            border: "1px solid #f1f1f1",
            boxShadow: "0 14px 40px rgba(0,0,0,0.06)",
            padding: 24,
            textAlign: "center",
            minWidth: 0,
          }}
        >
          {/* Avatar */}
          {profile.photoURL ? (
            <img
              src={profile.photoURL}
              alt="Profile"
              style={{
                width: 110,
                height: 110,
                borderRadius: "50%",
                objectFit: "cover",
                boxShadow: "0 3px 16px #ffd45255",
              }}
            />
          ) : (
            <FaUserCircle size={110} color="#e7ced5" />
          )}

          {editing && (
            <label
              htmlFor="avatar-upload"
              style={{ cursor: "pointer", display: "block", marginTop: 10 }}
            >
              <FaCamera /> Change Photo
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </label>
          )}

          {/* Form fields */}
          <div style={{ marginTop: 20, textAlign: "left" }}>
            <label style={{ fontWeight: 600 }}>Name:</label>
            <input
              ref={nameRef}
              disabled={!editing}
              type="text"
              value={profile.name}
              onChange={(e) =>
                setProfile((p) => ({ ...p, name: e.target.value }))
              }
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: 8,
                border: "1px solid #ccc",
                marginTop: 5,
              }}
            />
          </div>

          <div style={{ marginTop: 15, textAlign: "left" }}>
            <label style={{ fontWeight: 600 }}>Email:</label>
            <input
              disabled
              type="email"
              value={profile.email}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: 8,
                border: "1px solid #ccc",
                marginTop: 5,
              }}
            />
          </div>

          <div style={{ marginTop: 15, textAlign: "left" }}>
            <label style={{ fontWeight: 600 }}>Mobile:</label>
            <input
              disabled={!editing}
              type="tel"
              value={profile.mobile}
              onChange={(e) =>
                setProfile((p) => ({
                  ...p,
                  mobile: e.target.value.replace(/[^0-9]/g, ""),
                }))
              }
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: 8,
                border: "1px solid #ccc",
                marginTop: 5,
              }}
            />
          </div>

          {/* Buttons */}
          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 25,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {editing ? (
              <button
                onClick={handleSave}
                disabled={uploading}
                style={{
                  background: "linear-gradient(45deg, #27ae60, #2ecc71)",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: 25,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                <FaSave /> {uploading ? "Saving…" : "Save"}
              </button>
            ) : (
              <button
                onClick={() => setEditing(true)}
                style={{
                  background: "linear-gradient(45deg, #ffd452, #c79a5b)",
                  color: "#670D2B",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: 25,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                <FaEdit /> Edit
              </button>
            )}
            <button
              onClick={handleLogout}
              style={{
                background: "linear-gradient(45deg, #e74c3c, #c0392b)",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: 25,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>

        {/* ORDERS PANEL */}
        <div
          className="orders-card"
          style={{
            background: "#fff",
            borderRadius: 20,
            border: "1px solid #f1f1f1",
            boxShadow: "0 14px 40px rgba(0,0,0,0.06)",
            padding: 24,
            minWidth: 0, // ✅ prevents overflow on small screens
          }}
        >
          <h3
            style={{
              margin: 0,
              marginBottom: 14,
              color: "#670D2B",
              fontFamily: "'Playfair Display', serif",
            }}
          >
            Your Orders
          </h3>

          {/* Filters */}
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 14,
            }}
          >
            {["all", "ongoing", "completed", "cancelled"].map((k) => (
              <button
                key={k}
                onClick={() => setStatusFilter(k)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: "1px solid #eee",
                  cursor: "pointer",
                  background:
                    statusFilter === k ? "#ffe8ef" : "rgba(255,255,255,0.8)",
                  color: statusFilter === k ? "#7e1b2c" : "#444",
                  fontWeight: 700,
                  letterSpacing: ".02em",
                }}
              >
                {k === "all" ? "All" : k[0].toUpperCase() + k.slice(1)}
              </button>
            ))}
          </div>

          {/* Orders list */}
          {ordersLoading ? (
            <div style={{ padding: 12 }}>Loading orders…</div>
          ) : filteredOrders.length === 0 ? (
            <div style={{ padding: 12, color: "#666" }}>
              No orders yet. After a successful payment, your orders will
              appear here in real time.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {filteredOrders.map((o) => {
                const created =
                  o.createdAt?.toDate?.() ??
                  (o.createdAt?._seconds
                    ? new Date(o.createdAt._seconds * 1000)
                    : null);
                const dateStr = created ? created.toLocaleString() : "—";
                const total = Number(o.total || 0);
                const items = Array.isArray(o.items) ? o.items : o.cart || [];

                return (
                  <div
                    key={o.id}
                    style={{
                      border: "1px solid #f1f1f1",
                      borderRadius: 14,
                      padding: 14,
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      gap: 8,
                      alignItems: "center",
                      background: "#fff",
                      minWidth: 0,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, color: "#2a2a2a" }}>
                        Order #{prettyOrderId(o)}
                      </div>
                      <div style={{ fontSize: 13, color: "#666" }}>{dateStr}</div>
                      <div style={{ marginTop: 6 }}>{statusBadge(o.status)}</div>

                      {/* IDs */}
                      <div style={{ marginTop: 6, fontSize: 13, color: "#444" }}>
                        <div><strong>Order ID:</strong> {o.orderId || o.id || "—"}</div>
                        <div><strong>Payment ID:</strong> {o.paymentId || "—"}</div>
                      </div>

                      {/* Items summary */}
                      {items.length > 0 && (
                        <div
                          style={{ marginTop: 8, color: "#444", fontSize: 14, wordBreak: "break-word" }}
                        >
                          {items.slice(0, 3).map((it) => it.name).join(", ")}
                          {items.length > 3 ? "…" : ""}
                        </div>
                      )}

                      {/* Receipt button */}
                      <div style={{ marginTop: 10 }}>
                        <button
                          onClick={() => downloadReceipt(o)}
                          style={{
                            padding: "6px 12px",
                            borderRadius: 8,
                            border: "1px solid #eee",
                            background: "#fafafa",
                            cursor: "pointer",
                            fontWeight: 600,
                          }}
                        >
                          Download Receipt
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        textAlign: "right",
                        fontWeight: 800,
                        color: "#b0354c",
                        fontSize: 18,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {rupee(total)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 🔧 Responsive grid rules (override inline issues by removing them) */}
      <style>{`
        #profileGrid{
          display: grid;
          grid-template-columns: 1fr; /* ✅ default: stack on mobile */
          gap: 24px;
        }
        #profileGrid > .profile-card,
        #profileGrid > .orders-card { min-width: 0; } /* ✅ prevent overflow */
        @media (min-width: 1025px){
          #profileGrid{
            grid-template-columns: minmax(260px, 420px) 1fr; /* ✅ 2 columns on desktop */
          }
        }
        /* Mobile nav behavior */
        @media (max-width: 900px){
          .center-nav{ display:none !important; }
          .mobile-menu-icon{ display:inline-block !important; }
        }
      `}</style>
    </div>
  );
}
